'use client'

import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { getSchemaTemplate, processSchemaTemplate, getDefaultSchemaVariables } from '@/config/schema-templates'
import { seoCache } from '@/lib/seo-cache'

interface SchemaContextType {
  schemas: any[]
  addSchema: (schema: any) => void
  addSchemaFromTemplate: (templateId: string, variables?: Record<string, any>) => void
  addBreadcrumb: (pageTitle: string) => void
  clearSchemas: () => void
}

const SchemaContext = createContext<SchemaContextType | undefined>(undefined)

export function useSchema() {
  const context = useContext(SchemaContext)
  if (!context) {
    throw new Error('useSchema must be used within a SchemaProvider')
  }
  return context
}

interface SchemaProviderProps {
  children: React.ReactNode
}

export function SchemaProvider({ children }: SchemaProviderProps) {
  const pathname = usePathname()
  const [schemas, setSchemas] = useState<any[]>([])
  const [isHydrated, setIsHydrated] = useState(false)
  const addedSchemasRef = useRef<Set<string>>(new Set())
  const [seoData, setSeoData] = useState<any>(null)

  // Track hydration
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Load SEO data
  useEffect(() => {
    const loadSEOData = async () => {
      try {
        const data = await seoCache.getSEOData()
        setSeoData(data)
        generateSchemasFromConfig(data)
      } catch (error) {
        console.error('Failed to load SEO data:', error)
      }
    }

    loadSEOData()
  }, [pathname])

  const generateSchemasFromConfig = (data: any) => {
    const defaultVariables = getDefaultSchemaVariables()
    
    // NOTE: Global schemas are handled by GlobalSchemas component in layout.tsx
    // This prevents duplication between global and page schemas
    
    // Add page-specific schemas if enabled
    const currentPage = data.pages?.find((page: any) => page.page === pathname)
    if (currentPage?.schemas) {
      Object.entries(currentPage.schemas).forEach(([schemaId, config]: [string, any]) => {
        if (config.enabled) {
          const template = getSchemaTemplate(schemaId)
          if (template) {
            // Skip global schemas - they're handled by GlobalSchemas component
            if (template.category === 'global') {
              return
            }

            let variables = {
              ...defaultVariables,
              ...data.brand,
              pageTitle: currentPage.title,
              pageUrl: `${defaultVariables.domain}${pathname}`,
              title: currentPage.title,
              description: currentPage.description,
              keywords: currentPage.keywords,
              ...config.config
            }

            // Special handling for breadcrumb
            if (schemaId === 'breadcrumb' && pathname !== '/') {
              variables = {
                ...variables,
                pageTitle: currentPage.title || 'Trang',
                pageUrl: `${defaultVariables.domain}${pathname}`
              }
            }

            const processedSchema = processSchemaTemplate(template.template, variables)
            addSchema(processedSchema)
          }
        }
      })
    }
  }

  const addSchema = (schema: any) => {
    const schemaString = JSON.stringify(schema)
    // Simple hash function that works with Unicode characters
    let schemaHash = 0
    for (let i = 0; i < schemaString.length; i++) {
      const char = schemaString.charCodeAt(i)
      schemaHash = ((schemaHash << 5) - schemaHash) + char
      schemaHash = schemaHash & schemaHash // Convert to 32bit integer
    }
    const hashString = schemaHash.toString(36) // Convert to base36 string
    
    // Prevent duplicate schemas
    if (addedSchemasRef.current.has(hashString)) {
      return
    }
    
    addedSchemasRef.current.add(hashString)
    setSchemas(prev => [...prev, schema])
  }

  const addSchemaFromTemplate = (templateId: string, variables: Record<string, any> = {}) => {
    const template = getSchemaTemplate(templateId)
    if (!template) return

    const defaultVariables = getDefaultSchemaVariables()
    const finalVariables = {
      ...defaultVariables,
      ...seoData?.brand,
      ...variables
    }

    const processedSchema = processSchemaTemplate(template.template, finalVariables)
    addSchema(processedSchema)
  }

  const addBreadcrumb = (pageTitle: string) => {
    if (pathname === '/') return // No breadcrumb for homepage

    addSchemaFromTemplate('breadcrumb', {
      pageTitle,
      pageUrl: `${getDefaultSchemaVariables().domain}${pathname}`
    })
  }

  const clearSchemas = () => {
    setSchemas([])
    addedSchemasRef.current.clear()
  }

  // Inject schemas into head (only when schemas change and after hydration)
  useEffect(() => {
    if (!isHydrated) return // Wait for hydration to complete

    // Remove existing page schemas
    const existingPageSchemas = document.querySelectorAll('script[data-schema-type="page"]')
    existingPageSchemas.forEach(script => script.remove())

    // Deduplicate by content before adding
    const unique = Array.from(new Map(schemas.map(s => [JSON.stringify(s), s])).values())

    // Add new schemas
    unique.forEach((schema, index) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-schema-type', 'page')
      script.setAttribute('suppressHydrationWarning', '')
      script.textContent = JSON.stringify(schema, null, 2)
      document.head.appendChild(script)
    })
  }, [schemas, isHydrated])

  return (
    <SchemaContext.Provider value={{
      schemas,
      addSchema,
      addSchemaFromTemplate,
      addBreadcrumb,
      clearSchemas
    }}>
      {children}
    </SchemaContext.Provider>
  )
}

/**
 * Legacy component for backward compatibility
 * Now uses the new template system
 */
interface AddSchemaProps {
  schemas?: any | any[]
  pageTitle?: string
  noBreadcrumb?: boolean
}

export function AddSchema({ 
  schemas, 
  pageTitle, 
  noBreadcrumb = false 
}: AddSchemaProps) {
  const { addSchema, addBreadcrumb } = useSchema()
  const pathname = usePathname()

  useEffect(() => {
    // Add custom schemas
    if (schemas) {
      if (Array.isArray(schemas)) {
        schemas.forEach(schema => addSchema(schema))
      } else {
        addSchema(schemas)
      }
    }
    
    // Add breadcrumb schema (only for non-homepage pages)
    if (!noBreadcrumb && pathname !== '/' && pageTitle) {
      addBreadcrumb(pageTitle)
    }
  }, [schemas, pageTitle, noBreadcrumb, pathname, addSchema, addBreadcrumb])

  return null
} 